// src/components/Modals/ShowBankDetailsModal.component.tsx
import React from 'react';
import { Modal, Card, Button } from 'react-daisyui';
import { useModalsContext } from '../../../ModalsProvider/Modals.context';
import { useUserContext } from '../../../UserProvider/User.context';
import { eUserRole } from '../../../UserProvider/User.enums';
import { useSubscriptionContext } from '../../Subscription.context';
import './SubscriptionModals.styles.scss';
import { useToastContext } from '../../../ToastProvider/Toast.hook';
import { eAlertType } from '../../../ToastProvider/Toast.enum';

export default function ShowBankDetailsModal(): React.ReactElement | null {
  const { showBankDetailsModal, toggleBankDetailsModal } = useModalsContext();
  const { currentUserRole } = useUserContext();
  const { subscription } = useSubscriptionContext();

  const { addAlert } = useToastContext();
  const isOwner = currentUserRole === eUserRole.OWNER;

  // RET-1332: the account below is a hardcoded BBVA México / MXN account.
  // Never render it for non-MXN (e.g. USD) customers, regardless of how this
  // modal is opened. This was defense in depth alongside the matching currency
  // gate at PrevPastDueModal's call site — the ONLY place that opens this modal.
  // RET-1269 put that modal behind VITE_PAST_DUE_LADDER_FEATURE, so the pairing
  // now depends on the flag: with the ladder OFF the two gates still agree, and
  // with it ON this one stands alone (and nothing opens this modal at all until
  // the ladder grows its own bank-transfer entry point — when it does, it must
  // carry the same currency gate). Do not relax it.
  const isMxnTransferAvailable = subscription.currency?.toUpperCase() === 'MXN';
  if (!isMxnTransferAvailable) {
    return null;
  }

  const bankData = {
    bank: 'BBVA',
    name: 'Grupo Conexión Software SAPI de CV',
    clabe: '012680001172436508',
    account: '0117243650',
    cardNo: '4555 1130 0935 3287',
    note: 'Tu pago se verá reflejado dentro de 24 a 72 hrs.',
  };

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    addAlert({
      type: eAlertType.SUCCESS,
      message: 'Text copiado',
    });
  };

  return (
    <Modal open={showBankDetailsModal} backdrop={!isOwner}>
      <div className="ShowBankDetailsModal__container">
        <div className="ShowBankDetailsModal__header">
          <div className="flex flex-col items-center gap-2 text-center w-full">
            <p className="ShowBankDetailsModal__icon">🏦</p>
            <h2 className="text-lg font-semibold">
              Realiza tu transferencia a la siguiente cuenta
            </h2>
          </div>
          {!isOwner && (
            <span
              className="ShowBankDetailsModal__closeIcon"
              onClick={() => toggleBankDetailsModal(false)}
            >
              ×
            </span>
          )}
        </div>

        <div className="ShowBankDetailsModal__content">
          <Card bordered className="w-full">
            <Card.Body className="gap-4">
              <h3 className="text-primary font-semibold mb-1">
                Datos bancarios
              </h3>

              <div className="flex justify-between w-full text-left">
                <div>
                  <p className="font-medium text-sm text-gray-500">Banco</p>
                  <p className="font-semibold">{bankData.bank}</p>
                </div>
                <div>
                  <p className="font-medium text-sm text-gray-500">Nombre</p>
                  <p className="font-semibold">{bankData.name}</p>
                </div>
              </div>

              <div className="flex justify-between w-full text-left">
                <div>
                  <p className="font-medium text-sm text-gray-500">CLABE</p>
                  <p className="font-semibold flex items-center gap-2">
                    {bankData.clabe}
                    <button onClick={() => copyToClipboard(bankData.clabe)}>
                      📋
                    </button>
                  </p>
                </div>
                <div>
                  <p className="font-medium text-sm text-gray-500">Cuenta</p>
                  <p className="font-semibold flex items-center gap-2">
                    {bankData.account}
                    <button onClick={() => copyToClipboard(bankData.account)}>
                      📋
                    </button>
                  </p>
                </div>
              </div>

              <div className="text-left">
                <p className="font-medium text-sm text-gray-500">No. Tarjeta</p>
                <p className="font-semibold flex items-center gap-2">
                  {bankData.cardNo}
                  <button onClick={() => copyToClipboard(bankData.cardNo)}>
                    📋
                  </button>
                </p>
              </div>
            </Card.Body>
          </Card>

          <p className="text-primary text-sm text-center">{bankData.note}</p>
        </div>

        {isOwner && (
          <div className="ShowBankDetailsModal__actions">
            <Button
              color="primary"
              onClick={() => {
                toggleBankDetailsModal(false);
              }}
            >
              Entendido
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
